import { Price as PriceModel } from 'models/Price';
import { Checkbox, RadioButton } from 'components/Shared/Form';
import { CardPrice } from 'components/Shared/Price/themes/BGH/cardPrice';
import cn from 'classnames';
import styles from './installationAddon.scss';
import { ToolTipDialog, ToolTipLink, ToolTipModal } from 'components/Shared/ToolTip/themes/BGH-RD3';
import { NO_ELEMENT } from 'utils';
import Markdown from 'components/Shared/Markdown';
import { ReactComponent } from 'models/ReactComponent';
import { ProductPdf } from 'models/ProductPdf';
import { ReactNode } from 'react';

export interface TooltipProps {
  type: 'modal' | 'pdf' | 'dialog';
  onClick?: () => void;
  description?: ReactComponent;
  pdf?: ProductPdf;
}

export interface AddonProps {
  title: string;
  price?: PriceModel;
  isCheckbox?: boolean;
  isSelected: boolean;
  tooltip?: TooltipProps;
  value: string | number;
  name: string;
  onClick?: (props: unknown) => void;
}

const Marker = ({
  isCheckbox,
  name,
  isSelected
}: Pick<AddonProps, 'isCheckbox' | 'name' | 'isSelected'>) => {
  return isCheckbox ? (
    <Checkbox
      readOnly
      checked={isSelected}
      appearance="tertiary"
      color="charcoal"
      value="checkbox"
      name={name}
      className={styles.checkbox}
    />
  ) : (
    <RadioButton
      size="small"
      checked={isSelected}
      value="radio"
      name={name}
      className={styles.radioButton}
    />
  );
};

const Title = ({ title }: Pick<AddonProps, 'title'>) => {
  return <p className={styles.title}>{title}</p>;
};

interface TooltipContainerProps {
  children?: ReactNode;
}

const TooltipContainer = ({ children }: TooltipContainerProps) => {
  if (!children) {
    return NO_ELEMENT;
  }

  return <div className={styles.tooltipContainer}>{children}</div>;
};

const Tooltip = ({ tooltip }: Pick<AddonProps, 'tooltip'>) => {
  if (!tooltip) {
    return NO_ELEMENT;
  }

  if (tooltip.type === 'modal') {
    const { onClick } = tooltip;
    return <ToolTipModal onClick={onClick} />;
  }

  if (tooltip.type === 'pdf') {
    const { pdf } = tooltip;
    return <ToolTipLink href={pdf?.fileUrl || ''} />;
  }

  const { description } = tooltip;
  return <ToolTipDialog text={<Markdown {...description} />} />;
};

const Price = ({ price }: Pick<AddonProps, 'price'>) => {
  if (!price) {
    return NO_ELEMENT;
  }

  return (
    <div className={styles.priceContainer}>
      <CardPrice price={price} page="installation" />
    </div>
  );
};

export const InstallationAddon = ({
  price,
  title,
  isCheckbox,
  tooltip,
  value,
  isSelected,
  name,
  onClick
}: AddonProps) => {
  const handleOnClick = () => onClick && onClick(value);

  return (
    <button
      onClick={handleOnClick}
      className={cn(styles.container, {
        [styles.selected]: isSelected
      })}
    >
      <Marker isCheckbox={isCheckbox} name={name} isSelected={isSelected} />
      <Title title={title} />
      <TooltipContainer>
        <Tooltip tooltip={tooltip} />
      </TooltipContainer>
      <Price price={price} />
    </button>
  );
};
